import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bell,
  BellRing,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getActiveRestaurantId } from '../utils/roleAccess';
import {
  orderRequestService,
  OrderRequestNotification,
  WaiterCallNotification,
} from '../services/orderRequest';
import { useRestaurantWebSocket } from '../hooks/useRestaurantWebSocket';
import { useWebSocket } from '../contexts/WebSocketContext';

type StatusFilter = 'PENDING' | 'ALL';

type NotificationItem =
  | { type: 'order-request'; data: OrderRequestNotification; sortKey: number }
  | { type: 'waiter-call'; data: WaiterCallNotification; sortKey: number };

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  PENDING:      { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
  ACCEPTED:     { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Accepted' },
  REJECTED:     { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Rejected' },
  ACKNOWLEDGED: { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'Acknowledged' },
};

const PAGE_SIZE = 10;

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export const Notifications: React.FC = () => {
  const { currentTheme } = useTheme();
  const { connected } = useWebSocket();
  const activeRestaurantId = getActiveRestaurantId();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [orderRequests, setOrderRequests] = useState<OrderRequestNotification[]>([]);
  const [waiterCalls, setWaiterCalls] = useState<WaiterCallNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [actioning, setActioning] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const fetchAll = useCallback(async () => {
    if (!activeRestaurantId) return;
    setLoading(true);
    try {
      const [reqs, calls] = await Promise.all([
        orderRequestService.getAllRequests(activeRestaurantId),
        orderRequestService.getAllWaiterCalls(activeRestaurantId),
      ]);
      setOrderRequests(reqs);
      setWaiterCalls(calls);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [activeRestaurantId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // WebSocket — live notification updates
  useRestaurantWebSocket({
    onNotification: useCallback((msg: any) => {
      if (msg.type === 'ORDER_REQUEST_NEW') {
        setOrderRequests(prev => [msg.payload as OrderRequestNotification, ...prev]);
      } else if (msg.type === 'WAITER_CALL_NEW') {
        setWaiterCalls(prev => [msg.payload as WaiterCallNotification, ...prev]);
      } else if (msg.type === 'NOTIFICATION_STATUS_CHANGE') {
        const { id, kind, status } = msg.payload;
        if (kind === 'order-request') {
          setOrderRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
        } else if (kind === 'waiter-call') {
          setWaiterCalls(prev => prev.map(c => c.id === id ? { ...c, status } : c));
        }
      }
    }, []),
  });

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [statusFilter]);

  const handleAccept = async (id: string) => {
    setActioning(id);
    try {
      await orderRequestService.acceptRequest(id);
      await fetchAll();
    } finally {
      setActioning(null);
    }
  };

  const handleReject = async (id: string) => {
    setActioning(id);
    try {
      await orderRequestService.rejectRequest(id);
      await fetchAll();
    } finally {
      setActioning(null);
    }
  };

  const handleAcknowledge = async (id: string) => {
    setActioning(id);
    try {
      await orderRequestService.acknowledgeWaiterCall(id);
      await fetchAll();
    } finally {
      setActioning(null);
    }
  };

  // Build unified sorted list
  const allItems: NotificationItem[] = [
    ...orderRequests.map(r => ({
      type: 'order-request' as const,
      data: r,
      sortKey: new Date(r.createdAt).getTime(),
    })),
    ...waiterCalls.map(c => ({
      type: 'waiter-call' as const,
      data: c,
      sortKey: new Date(c.createdAt).getTime(),
    })),
  ].sort((a, b) => b.sortKey - a.sortKey);

  const filtered = statusFilter === 'PENDING'
    ? allItems.filter(item =>
        item.type === 'order-request'
          ? item.data.status === 'PENDING'
          : item.data.status === 'PENDING'
      )
    : allItems;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const pendingCount = orderRequests.filter(r => r.status === 'PENDING').length
    + waiterCalls.filter(c => c.status === 'PENDING').length;

  if (!activeRestaurantId) {
    return (
      <div className="p-8 text-center" style={{ color: currentTheme.colors.text.secondary }}>
        <Bell className="h-12 w-12 mx-auto mb-3 opacity-40" />
        <p>Please select a restaurant to view notifications.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          {pendingCount > 0
            ? <BellRing className="h-6 w-6 text-red-500" />
            : <Bell className="h-6 w-6" style={{ color: currentTheme.colors.text.secondary }} />
          }
          <div>
            <h1 className="text-xl font-bold" style={{ color: currentTheme.colors.text.primary }}>
              Notifications
            </h1>
            <p className="text-xs" style={{ color: pendingCount > 0 ? '#EF4444' : currentTheme.colors.text.secondary }}>
              {pendingCount > 0
                ? `${pendingCount} pending action${pendingCount !== 1 ? 's' : ''}`
                : `${filtered.length} total`
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 text-xs font-medium ${connected ? 'text-green-600' : 'text-gray-400'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-green-500' : 'bg-gray-400'}`} />
            {connected ? 'Live' : 'Offline'}
          </span>
          <button
            onClick={fetchAll}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: currentTheme.colors.primary[100], color: currentTheme.colors.primary[700] }}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 mb-5">
        {(['PENDING', 'ALL'] as StatusFilter[]).map(f => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className="px-4 py-1.5 rounded-full text-sm font-medium transition-colors"
            style={{
              backgroundColor: statusFilter === f ? currentTheme.colors.primary[500] : currentTheme.colors.background.secondary,
              color: statusFilter === f ? '#fff' : currentTheme.colors.text.secondary,
            }}
          >
            {f === 'PENDING' ? `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}` : `All History (${allItems.length})`}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="h-6 w-6 animate-spin" style={{ color: currentTheme.colors.text.secondary }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center" style={{ color: currentTheme.colors.text.secondary }}>
          <Bell className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No notifications found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {paginated.map(item =>
            item.type === 'order-request'
              ? <OrderRequestCard
                  key={item.data.id}
                  req={item.data}
                  actioning={actioning}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  theme={currentTheme}
                />
              : <WaiterCallCard
                  key={item.data.id}
                  call={item.data}
                  actioning={actioning}
                  onAcknowledge={handleAcknowledge}
                  theme={currentTheme}
                />
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
          <p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
            Page {page} of {totalPages} · {filtered.length} notifications
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg disabled:opacity-40 transition-colors hover:bg-gray-100"
              style={{ color: currentTheme.colors.text.secondary }}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                  acc.push('...');
                }
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...'
                  ? <span key={`ellipsis-${idx}`} className="px-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}>…</span>
                  : <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      className="min-w-[30px] h-[30px] rounded-lg text-xs font-medium transition-colors"
                      style={{
                        backgroundColor: page === p ? currentTheme.colors.primary[500] : 'transparent',
                        color: page === p ? '#fff' : currentTheme.colors.text.secondary,
                      }}
                    >
                      {p}
                    </button>
              )
            }

            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg disabled:opacity-40 transition-colors hover:bg-gray-100"
              style={{ color: currentTheme.colors.text.secondary }}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Order Request Card ─────────────────────────────────────────────────────────
interface OrderRequestCardProps {
  req: OrderRequestNotification;
  actioning: string | null;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  theme: any;
}

const OrderRequestCard: React.FC<OrderRequestCardProps> = ({ req, actioning, onAccept, onReject, theme }) => {
  const isPending = req.status === 'PENDING';
  const statusStyle = STATUS_COLORS[req.status] ?? STATUS_COLORS.PENDING;
  const total = req.items.reduce((s, i) => s + i.unitPrice * i.quantity, 0);

  return (
    <div
      className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] overflow-hidden"
      style={{
        borderColor: isPending ? '#93C5FD' : theme.colors.border.primary,
        backgroundColor: isPending ? '#EFF6FF' : theme.colors.background.secondary,
      }}
    >
      {/* Card top */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold" style={{ color: theme.colors.text.primary }}>
              🪑 {req.tableName || req.tableId}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
              {statusStyle.label}
            </span>
            <span className="text-xs text-gray-400">Order Request</span>
          </div>
          <div className="flex items-center gap-1 text-xs flex-shrink-0" style={{ color: theme.colors.text.secondary }}>
            <Clock className="h-3 w-3" />
            {timeAgo(req.createdAt)}
          </div>
        </div>
        <p className="text-xs mt-1" style={{ color: theme.colors.text.secondary }}>
          {req.customerName} · {req.customerMobile}
        </p>
        <p className="text-xs mt-0.5" style={{ color: theme.colors.text.secondary }}>
          {formatTime(req.createdAt)}
        </p>
      </div>

      {/* Divider */}
      <div className="border-t mx-4" style={{ borderColor: isPending ? '#BFDBFE' : theme.colors.border.primary }} />

      {/* Items */}
      <div className="px-4 pt-3 pb-2 space-y-2">
        {req.items.map((item, idx) => (
          <div key={idx} className="flex items-start justify-between text-sm">
            <div className="flex-1 min-w-0">
              <p className="font-medium" style={{ color: theme.colors.text.primary }}>{item.itemName}</p>
              {item.specialNotes && (
                <p className="text-xs mt-0.5 italic" style={{ color: theme.colors.text.secondary }}>
                  Note: {item.specialNotes}
                </p>
              )}
            </div>
            <div className="text-right ml-4 flex-shrink-0">
              <p className="text-xs" style={{ color: theme.colors.text.secondary }}>× {item.quantity}</p>
              <p className="font-semibold text-sm" style={{ color: theme.colors.text.primary }}>
                ₹{(item.unitPrice * item.quantity).toFixed(2)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Total */}
      <div
        className="mx-4 py-2 flex justify-between items-center border-t"
        style={{ borderColor: isPending ? '#BFDBFE' : theme.colors.border.primary }}
      >
        <span className="text-sm font-bold" style={{ color: theme.colors.text.primary }}>Total</span>
        <span className="text-sm font-bold" style={{ color: theme.colors.primary[700] }}>
          ₹{total.toFixed(2)}
        </span>
      </div>

      {/* Action buttons — only for pending */}
      {isPending && (
        <div className="px-4 pb-4 flex gap-2">
          <button
            onClick={() => onAccept(req.id)}
            disabled={actioning === req.id}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold disabled:opacity-60 transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            Accept
          </button>
          <button
            onClick={() => onReject(req.id)}
            disabled={actioning === req.id}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold disabled:opacity-60 transition-colors"
          >
            <XCircle className="h-4 w-4" />
            Reject
          </button>
        </div>
      )}
    </div>
  );
};

// ── Waiter Call Card ───────────────────────────────────────────────────────────
interface WaiterCallCardProps {
  call: WaiterCallNotification;
  actioning: string | null;
  onAcknowledge: (id: string) => void;
  theme: any;
}

const WaiterCallCard: React.FC<WaiterCallCardProps> = ({ call, actioning, onAcknowledge, theme }) => {
  const isPending = call.status === 'PENDING';
  const statusStyle = STATUS_COLORS[call.status] ?? STATUS_COLORS.PENDING;

  return (
    <div
      className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] px-4 py-3"
      style={{
        borderColor: isPending ? '#FCD34D' : theme.colors.border.primary,
        backgroundColor: isPending ? '#FFFBEB' : theme.colors.background.secondary,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold" style={{ color: theme.colors.text.primary }}>
              🔔 {call.tableName}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
              {statusStyle.label}
            </span>
            <span className="text-xs text-gray-400">Waiter Call</span>
          </div>
          <p className="text-xs mt-1" style={{ color: theme.colors.text.secondary }}>
            Customer needs assistance at this table
          </p>
          <div className="flex items-center gap-1 mt-1 text-xs" style={{ color: theme.colors.text.secondary }}>
            <Clock className="h-3 w-3" />
            {timeAgo(call.createdAt)} · {formatTime(call.createdAt)}
          </div>
        </div>
        {isPending && (
          <button
            onClick={() => onAcknowledge(call.id)}
            disabled={actioning === call.id}
            className="flex-shrink-0 px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold disabled:opacity-60 transition-colors"
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};
